import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Outlet, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AuthLayoutProps {
  children?: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { user, isLoading, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isCheckingUserRecord, setIsCheckingUserRecord] = useState(false);
  const hasSignedOutRef = useRef(false);

  // Check if there's an invitation code in the URL
  const hasInvitationCode = searchParams.has('code');

  // Check if this is the onboarding page
  const isOnboardingPage = location.pathname === '/onboarding';

  useEffect(() => {
    const abortController = new AbortController();

    const handleInvitationWithAuthenticatedUser = async () => {
      // Only check if:
      // 1. User is authenticated
      // 2. There's an invitation code
      // 3. We haven't already signed out
      // 4. Not already checking or signing out
      if (
        !isLoading &&
        user &&
        hasInvitationCode &&
        !isSigningOut &&
        !isCheckingUserRecord &&
        !hasSignedOutRef.current
      ) {
        setIsCheckingUserRecord(true);

        try {
          const companyCode = searchParams.get('code');

          // First, check if user JUST completed onboarding (within last 30 seconds)
          const onboardingCompletedAt = sessionStorage.getItem('onboarding_completed_at');
          const onboardingCompletedCode = sessionStorage.getItem('onboarding_completed_code');

          if (onboardingCompletedAt && onboardingCompletedCode === companyCode) {
            const completedTime = parseInt(onboardingCompletedAt);
            const now = Date.now();

            // If onboarding was completed within last 30 seconds, don't sign out
            if (now - completedTime < 30000) {
              console.log('AuthLayout: User just completed onboarding, not signing out');
              setIsCheckingUserRecord(false);

              // Clean up session storage after grace period
              setTimeout(() => {
                sessionStorage.removeItem('onboarding_completed_at');
                sessionStorage.removeItem('onboarding_completed_code');
              }, 5000);

              return;
            }
          }

          // Check if this invitation has already been accepted
          const { data: invitationData } = await supabase
            .from('tenant_invitations')
            .select('accepted_at, tenant_id')
            .eq('company_code', companyCode)
            .abortSignal(abortController.signal)
            .maybeSingle();

          // If invitation is accepted AND user's tenant_id matches, they just completed onboarding
          if (invitationData?.accepted_at && invitationData?.tenant_id) {
            // Check if user belongs to this tenant
            const { data: userData } = await supabase
              .from('users')
              .select('tenant_id')
              .eq('id', user.id)
              .eq('tenant_id', invitationData.tenant_id)
              .abortSignal(abortController.signal)
              .maybeSingle();

            if (userData) {
              console.log('AuthLayout: User belongs to invitation tenant, not signing out');
              setIsCheckingUserRecord(false);
              return;
            }
          }

          // Check if user has a record in the users table
          // If they don't, they're a brand new signup in progress
          // If they do, they're an existing user trying to use a new invitation
          const { data: userData, error } = await supabase
            .from('users')
            .select('id')
            .eq('id', user.id)
            .abortSignal(abortController.signal)
            .maybeSingle();

          if (error) {
            console.error('Error checking user record:', error);
            setIsCheckingUserRecord(false);
            return;
          }

          // If user record exists, this is an existing user accessing a new invitation
          // Sign them out so they can complete the new signup
          if (userData) {
            console.log(
              'AuthLayout: Existing user with database record accessing invitation link, signing out to allow new signup...'
            );
            setIsSigningOut(true);
            hasSignedOutRef.current = true;

            try {
              await signOut({ skipNavigation: true });
              toast({
                title: 'Signed out',
                description: 'You have been signed out to complete the invitation signup.',
              });
              // Keep isSigningOut true a bit longer to prevent flash
              setTimeout(() => {
                setIsSigningOut(false);
                setIsCheckingUserRecord(false);
              }, 500);
            } catch (signOutErr) {
              console.error('Error signing out:', signOutErr);
              toast({
                title: 'Error',
                description: 'Failed to sign out. Please try again.',
                variant: 'destructive',
              });
              setIsSigningOut(false);
              setIsCheckingUserRecord(false);
              hasSignedOutRef.current = false;
            }
          } else {
            // User record doesn't exist yet - this is a brand new signup in progress
            // Don't sign them out
            console.log(
              'AuthLayout: New signup in progress (no database record yet), not signing out'
            );
            setIsCheckingUserRecord(false);
          }
        } catch (err) {
          if ((err as Error)?.name === 'AbortError') return;
          console.error('Error in handleInvitationWithAuthenticatedUser:', err);
          setIsCheckingUserRecord(false);
        }
      }
    };

    handleInvitationWithAuthenticatedUser();

    return () => {
      abortController.abort();
    };
  }, [
    user,
    isLoading,
    hasInvitationCode,
    signOut,
    isSigningOut,
    isCheckingUserRecord,
    toast,
    searchParams,
  ]);

  // Safety timeout: if isCheckingUserRecord stays true for more than 5s, force-release it
  useEffect(() => {
    if (!isCheckingUserRecord) return;

    const timeout = setTimeout(() => {
      console.warn(
        '[AuthLayout] Safety timeout: isCheckingUserRecord stuck for 5s, forcing release'
      );
      setIsCheckingUserRecord(false);
      setIsSigningOut(false);
    }, 5_000);

    return () => clearTimeout(timeout);
  }, [isCheckingUserRecord]);

  // Reset the signout ref when invitation code changes
  useEffect(() => {
    const code = searchParams.get('code');
    return () => {
      // Reset on unmount or code change
      if (!code) {
        hasSignedOutRef.current = false;
      }
    };
  }, [searchParams]);

  // Show loading state while checking auth, checking user record, or signing out
  if (isLoading || isSigningOut || isCheckingUserRecord) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          {isSigningOut && (
            <p className="text-sm text-muted-foreground">Signing out to complete invitation...</p>
          )}
        </div>
      </div>
    );
  }

  // If user is already authenticated and NO invitation code, redirect to dashboard
  // But if we just signed out (hasSignedOutRef.current is true), don't redirect
  if (!isLoading && user && !hasInvitationCode && !hasSignedOutRef.current) {
    return <Navigate to="/dashboard" />;
  }

  // Onboarding page has its own layout, so render without wrapper
  if (isOnboardingPage) {
    return <>{children || <Outlet />}</>;
  }

  // For login/signup, use consistent branding with onboarding
  return (
    <div className="min-h-screen bg-gradient-to-b from-background/60 to-background">
      <div className="w-full px-4 pt-12 pb-8">
        <a href="/" className="mb-8 flex items-center justify-center gap-2">
          <img src="/logo-32.png" alt="HireMetrics Logo" className="h-9 w-9" />
          <div className="font-semibold text-2xl">InterviewAI</div>
        </a>

        <div className="w-full max-w-[95%] sm:max-w-[30rem] mx-auto">
          <div className="bg-card/50 backdrop-blur-sm px-6 py-8 sm:px-12 sm:py-10 rounded-xl border shadow-sm">
            {children || <Outlet />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
